<template>
    <div>
        <div class="ml-2">
            <h1 v-if="id" class="text-3xl">
                Detalle de proveedor

                <strong class="text-theme-primary">
                    {{ itemComponent.name }}
                </strong>
            </h1>
            <h1 v-else class="text-3xl">Nuevo proveedor</h1>
            <div class="flex w-1/2 justify-between">
                <strong class="flex items-center gap-1">
                    ID <uuid-display :value="itemComponent.id" />
                </strong>
            </div>
        </div>

        <custom-navbar
            id="tabs-products"
            v-model="activeTab"
            center-collapsed
            not-line
        >
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/suppliers"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="id"
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> General </span>
                        <span>&nbsp;⚙</span>
                    </div>
                </custom-navbar-item>
            </template>
            <template #right>
                <custom-switch
                    v-if="$ability.hasPermission('supplier:Update') && id"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)">
            <div v-if="id" class="flex flex-wrap">
                <div
                    class="w-full md:w-1/2 mt-8 p-0 flex justify-around items-center"
                >
                    <span class="text-left">
                        📆💾 {{ itemComponent.createdAt | date }}
                    </span>
                    <span class="text-left">
                        📆✏ {{ itemComponent.updatedAt | date }}
                    </span>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre *"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName && introValidators.name
                                ? 'Nombre de proveedor invalido'
                                : ''
                        "
                        @blur="introValidators.name = true"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.phone"
                        class="w-full"
                        label="Celular de contacto"
                        type="number"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidPhone && introValidators.phone
                                ? 'Celular invalido'
                                : ''
                        "
                        @blur="introValidators.phone = true"
                    />
                </div>

                <div v-if="!id" class="w-full px-2 mt-4">
                    <custom-checkbox v-model="showMoreFields">
                        Agregar más datos
                    </custom-checkbox>
                </div>
            </div>

            <div v-if="id || showMoreFields" class="flex flex-wrap">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.summary"
                        label="Descripción"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <identification-type-selector
                        v-model="itemComponent.identificationType"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.identificationNumber"
                        class="w-full"
                        label="Número de identificación"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <city-code-selector
                        v-model="itemComponent.cityCode"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.address"
                        class="w-full"
                        label="Dirección Ej. Cll 42 # 12 - 8"
                        placeholder="Cll 42 # 12 - 8"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidAddress && introValidators.address
                                ? 'Dirección invalida'
                                : ''
                        "
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.addressComplement"
                        class="w-full"
                        label="Complemento de Dirección Ej. Local 123"
                        placeholder="Local 123"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.neighborhood"
                        class="w-full"
                        label="Barrio / Localidad"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.coordinates"
                        class="w-full"
                        label="Coordenadas Ej. 4.6097,-74.0817"
                        placeholder="4.6097,-74.0817"
                        :disabled="!enabledUpdate"
                    />
                </div>
            </div>

            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button
                        v-if="!id"
                        size="large"
                        @click="createSupplierComponent"
                    >
                        Crear proveedor
                    </app-button>
                    <app-button v-else @click="updateSupplierComponent">
                        Actualizar proveedor
                    </app-button>
                </div>
            </div>
        </div>

        <app-dialog v-model="openActiveModal" @close="closeActiveModal">
            <template #header>
                <h2 class="text-theme-primary text-2xl">
                    Quieres habilitar la proveedor ?
                </h2>
            </template>
            <div class="my-3 mx-2">
                <div class="w-10/12 mx-auto">
                    <span>
                        Recuerda llenar la información del proveedor, de esta
                        manera será encontrado más fácil. Selecciona el peso de
                        ordenamiento, la proveedor con peso 1 será el primero en
                        aparecer en el listado.
                    </span>
                </div>

                <custom-input
                    v-model="itemComponent.weight"
                    class="w-8/12 mx-auto mt-4"
                    label="Peso"
                    type="number"
                    step="1"
                    min="1"
                />
            </div>
            <template #footer>
                <div class="con-footer flex justify-center my-2">
                    <app-button @click="updateSupplierActiveComponent">
                        Activar proveedor
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { addressRegex, phoneRegex } from "@/common/lib/regex";
import {
    removeEmptyFields,
    validateConfirmationMessage
} from "@/common/lib/transformers";

import IdentificationTypeSelector from "@/components/users/IdentificationTypeSelector.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "SupplierDetail",
    components: {
        AppDialog,
        CityCodeSelector,
        IdentificationTypeSelector,
        CustomInput,
        UuidDisplay
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        activeTab: "general",
        $tabsWhitelist: ["general"],
        id: "",
        isLoading: false,
        enabledUpdate: false,
        loadedAllData: false,
        openActiveModal: false,
        showMoreFields: false,
        itemComponent: {
            id: "",
            name: "",
            phone: "",
            identificationType: "",
            identificationNumber: "",
            summary: "",
            address: "",
            addressComplement: "",
            cityCode: "",
            neighborhood: "",
            coordinates: ""
        },
        introValidators: {
            phone: false,
            address: false,
            name: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidAddress() {
            return (
                !this.itemComponent.address ||
                addressRegex.test(this.itemComponent.address)
            );
        },
        isValidPhone() {
            return (
                !this.itemComponent.phone ||
                phoneRegex.test(this.itemComponent.phone)
            );
        },
        isValidName() {
            return this.itemComponent.name.length;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("supplier:Get", "/app/suppliers")))
                return;
            const id = this.$route.params.id;
            this.id = id;
            await this.getBaseItem(id);
        } else if ("idClone" in this.$route.params) {
            await verifyPagePermission("supplier:Create", "/app/suppliers");
            this.id = 0;
            this.enabledUpdate = true;
            this.showMoreFields = true;
            const idTmp = this.$route.params.idClone;
            await this.getBaseItem(idTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.name} CLONE`,
                active: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando un proveedor",
                icon: "info",
                showCloseButton: true
            });
        } else {
            await verifyPagePermission("supplier:Create", "/app/suppliers");
            this.enabledUpdate = true;
            this.loadedAllData = true;
        }
    },
    methods: {
        ...mapActions("suppliers", [
            "getSupplierById",
            "updateSupplier",
            "deleteSupplier",
            "createSupplier",
            "updateSupplierActive"
        ]),
        closeActiveModal() {
            this.itemComponent.active = false;
        },
        validateToggleActive() {
            if (!this.itemComponent.active) {
                return this.updateSupplierActiveComponent();
            }
            this.openActiveModal = true;
        },
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.getSupplierById({ id });
                this.itemComponent = {
                    ...data
                };
                this.loadedAllData = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/suppliers");
                }
            }
        },
        async updateSupplierActiveComponent() {
            this.openActiveModal = false;
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateSupplierActive(this.itemComponent);
                this.$sound.play();
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateSupplierComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateSupplier(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteSupplierComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas seguro deseas eliminar el proveedor",
                text: "Los proveedores solo deben eliminarse en caso de que se esten haciendo pruebas, eres consiente de lo que estas haciendo ?. Escribe 'Si' para eliminar",
                icon: "warning",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteSupplier({
                        id: this.id
                    });
                    this.$router.push("/app/suppliers");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async createSupplierComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createSupplier(this.getDataSend());
                this.$sound.play();
                this.$loading.hide();

                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${data.id}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de proveedores",
                    cancelButtonText: "Nuevo proveedor"
                });
                if (dialogResult.isConfirmed) {
                    this.id = data.id;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/suppliers/detail/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/suppliers/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        phone: false,
                        name: false
                    };
                    this.itemComponent = {
                        id: "",
                        name: "",
                        phone: "",
                        identificationType: "",
                        identificationNumber: "",
                        summary: "",
                        address: "",
                        addressComplement: "",
                        cityCode: "",
                        neighborhood: "",
                        coordinates: ""
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/suppliers");
                }
            }
        },
        validateData() {
            this.introValidators = {
                phone: true,
                name: true
            };

            const toComplete = [];
            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Proveedor invalido",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            const { name, phone } = this.itemComponent;
            return removeEmptyFields({
                ...(this.id || this.showMoreFields
                    ? this.itemComponent
                    : { name, phone }),
                id: this.id
            });
        }
    }
};
</script>
